// Horizontal-scroll leader cards for the story stats. A category only renders
// when at least one player has the stat (pre-extension imports skip cleanly).

import React from 'react';
import { GS, cardShadow } from '../../lib/designTokens';
import { fmtStat, StatKind } from '../../lib/stat-format';
import { shortName } from './ui';
import type { SeasonData } from './useSeasonStats';

interface LeaderDef {
  key: string;
  label: string;
  kind: StatKind;
  minPa?: number;
  value: (b: any) => number | null | undefined;
}

const LEADERS: LeaderDef[] = [
  { key: 'qabPct', label: 'QAB%', kind: 'pct', minPa: 8, value: b => b.qabPct },
  { key: 'sb', label: 'Stolen Bases', kind: 'count', value: b => b.sb > 0 ? b.sb : null },
  { key: 'hhb', label: 'Hard-Hit', kind: 'count', value: b => b.hhb },
  { key: 'baRisp', label: 'BA / RISP', kind: 'avg3', minPa: 8, value: b => b.baRisp },
  { key: 'bb', label: 'Walks', kind: 'count', value: b => b.bb > 0 ? b.bb : null },
  { key: 'ops', label: 'OPS', kind: 'avg3', minPa: 8, value: b => b.ops },
];

export const LeadersRow = ({ data }: { data: SeasonData }) => {
  const cards = LEADERS.map(def => {
    let best: { player: any; v: number } | null = null;
    Object.values(data.stats).forEach((s: any) => {
      const b = s.batting;
      if (def.minPa && (b.pa || 0) < def.minPa) return;
      const v = def.value(b);
      if (v == null) return;
      if (!best || v > best.v) best = { player: s.player, v };
    });
    return best ? { def, ...(best as { player: any; v: number }) } : null;
  }).filter((c): c is NonNullable<typeof c> => c != null);

  if (cards.length === 0) return null;

  return (
    // A scroller clips its overflow, shadows included: the padding makes room for
    // the card shadow and the negative margins cancel it, so the layout doesn't move.
    <div className="flex gap-2.5 overflow-x-auto -mx-2 px-2 -mt-2 pt-2 -mb-4 pb-5" style={{ scrollbarWidth: 'none' }}>
      {cards.map(({ def, player, v }) => (
        <div
          key={def.key}
          className="shrink-0 w-[118px] rounded-2xl p-3.5"
          style={{ background: GS.panel, boxShadow: cardShadow }}
        >
          <p className="text-[9px] font-black uppercase" style={{ color: GS.ink3, letterSpacing: '0.1em' }}>{def.label}</p>
          <p className="text-[22px] font-black leading-none my-2" style={{ color: GS.greenInk }}>{fmtStat(v, def.kind)}</p>
          <p className="text-xs font-extrabold truncate" style={{ color: GS.ink }}>{shortName(player.name)}</p>
          <p className="text-[10px] font-bold mt-0.5" style={{ color: GS.ink3 }}>#{player.number}</p>
        </div>
      ))}
    </div>
  );
};
